import { SupabaseAuth, getSupabaseClient } from '@/utils/supabase';
import { message } from 'antd';

export interface LoginParams {
  email: string;
  password: string;
}

export interface RegisterParams {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  firebase_token?: string;
  stations?: string[];
  permissions?: string[];
}

// Auth service using Supabase
export const authService = {
  // Login user
  login: async (params: LoginParams) => {
    const supabase = getSupabaseClient();
    
    try {
      // Step 1: Authenticate with Supabase Auth
      const { data, error } = await SupabaseAuth.signIn(params.email, params.password);
      
      if (error) {
        throw error;
      }

      if (!data.user || !data.session) {
        return {
          success: false,
          message: 'Login failed',
        };
      }

      // Step 1.5: Check if email is confirmed
      if (!data.user.email_confirmed_at) {
        // Email not confirmed, redirect to OTP verification
        return {
          success: false,
          requiresVerification: true,
          email: data.user.email,
          userId: data.user.id,
          message: 'Email chưa được xác thực. Vui lòng nhập mã OTP.',
        };
      }

      // Step 2: Fetch user info from public.users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('user_id', data.user.id)
        .single();

      if (userError) {
        console.warn('Could not fetch user data from public.users:', userError);
        // Continue with auth data only if public.users fetch fails
      }

      // Step 3: Merge auth user with public.users data
      const fullUserData = {
        id: data.user.id,
        email: data.user.email,
        first_name: userData?.first_name || data.user.user_metadata?.first_name || '',
        last_name: userData?.last_name || data.user.user_metadata?.last_name || '',
        avatar_url: userData?.avatar_url || data.user.user_metadata?.avatar_url || '',
        permissions: userData?.permissions || [], // Get permissions from public.users
        stations: userData?.stations || [],
        firebase_token: userData?.firebase_token,
        created_at: userData?.created_at,
        updated_at: userData?.updated_at,
      };

      // Step 4: Store user info and token
      localStorage.setItem('token', data.session.access_token);
      localStorage.setItem('user', JSON.stringify(fullUserData));

      return {
        success: true,
        data: fullUserData,
        authUser: data.user,
      };
    } catch (error: any) {
      message.error(error.message || 'Login failed');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Register user - creates both auth.users and public.users
  register: async (params: RegisterParams) => {
    const supabase = getSupabaseClient();
    
    try {
      // Step 1: Create user in auth.users with OTP email (no auto-confirm)
      const { data: authData, error: authError } = await SupabaseAuth.adminCreateUser(
        params.email,
        params.password,
        {
          first_name: params.first_name,
          last_name: params.last_name,
          avatar_url: params.avatar_url,
        },
        false // autoConfirm = false, user must verify email with OTP
      );

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error('User creation failed');
      }

      // Step 2: Create corresponding record in public.users
      const now = Math.floor(Date.now() / 1000); // Unix timestamp in seconds (int8)
      const { error: publicError } = await supabase
        .from('users')
        .insert({
          user_id: authData.user.id, // Match with auth.users.id
          first_name: params.first_name,
          last_name: params.last_name,
          avatar_url: params.avatar_url || null,
          firebase_token: params.firebase_token || null,
          stations: params.stations || null,
          permissions: params.permissions || null,
          created_at: now,
          updated_at: now,
        });

      if (publicError) {
        // If public.users creation fails, we should handle cleanup
        console.error('Error creating public user:', publicError);
        message.error('Tạo thông tin user thất bại: ' + publicError.message);
        throw publicError;
      }

      message.success('Đăng ký thành công!');
      return {
        success: true,
        data: authData.user,
      };
    } catch (error: any) {
      message.error(error.message || 'Đăng ký thất bại');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Logout user
  logout: async () => {
    try {
      const { error } = await SupabaseAuth.signOut();
      
      if (error) {
        throw error;
      }

      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      return {
        success: true,
      };
    } catch (error: any) {
      message.error(error.message || 'Logout failed');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const { user, error } = await SupabaseAuth.getCurrentUser();
      
      if (error) {
        throw error;
      }

      if (user) {
        return {
          success: true,
          data: {
            id: user.id,
            email: user.email,
            name: user.user_metadata?.name || user.email,
            avatar: user.user_metadata?.avatar || '',
            permissions: user.user_metadata?.permissions || [],
          },
        };
      }

      return {
        success: false,
        message: 'User not found',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Reset password
  resetPassword: async (email: string) => {
    try {
      const { error } = await SupabaseAuth.resetPassword(email);
      
      if (error) {
        throw error;
      }

      message.success('Password reset email sent! Please check your inbox.');
      return {
        success: true,
      };
    } catch (error: any) {
      message.error(error.message || 'Password reset failed');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Update user profile
  updateProfile: async (attributes: any) => {
    try {
      const { data, error } = await SupabaseAuth.updateUser(attributes);
      
      if (error) {
        throw error;
      }

      if (data.user) {
        // Update localStorage
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = {
          ...currentUser,
          ...attributes.data,
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        message.success('Profile updated successfully');
        return {
          success: true,
          data: data.user,
        };
      }

      return {
        success: false,
        message: 'Profile update failed',
      };
    } catch (error: any) {
      message.error(error.message || 'Profile update failed');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Verify OTP
  verifyOTP: async (email: string, token: string) => {
    const supabase = getSupabaseClient();
    
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (error) {
        throw error;
      }

      if (data.user && data.session) {
        // Fetch user info from public.users table
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('user_id', data.user.id)
          .single();

        if (userError) {
          console.warn('Could not fetch user data from public.users:', userError);
        }

        // Merge auth user with public.users data
        const fullUserData = {
          id: data.user.id,
          email: data.user.email,
          first_name: userData?.first_name || data.user.user_metadata?.first_name || '',
          last_name: userData?.last_name || data.user.user_metadata?.last_name || '',
          avatar_url: userData?.avatar_url || data.user.user_metadata?.avatar_url || '',
          permissions: userData?.permissions || [],
          stations: userData?.stations || [],
          firebase_token: userData?.firebase_token,
          created_at: userData?.created_at,
          updated_at: userData?.updated_at,
        };

        // Store user info and token
        localStorage.setItem('token', data.session.access_token);
        localStorage.setItem('user', JSON.stringify(fullUserData));

        return {
          success: true,
          data: fullUserData,
        };
      }

      return {
        success: false,
        message: 'Verification failed',
      };
    } catch (error: any) {
      message.error(error.message || 'OTP verification failed');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Resend verification OTP
  resendVerificationOTP: async (email: string) => {
    const supabase = getSupabaseClient();
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        message: 'OTP đã được gửi lại',
      };
    } catch (error: any) {
      message.error(error.message || 'Resend OTP failed');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Send password reset OTP
  sendPasswordResetOTP: async (email: string) => {
    try {
      const { error } = await SupabaseAuth.sendPasswordResetOTP(email);
      
      if (error) {
        throw error;
      }

      message.success('Mã OTP đã được gửi đến email của bạn');
      return {
        success: true,
        message: 'OTP sent successfully',
      };
    } catch (error: any) {
      message.error(error.message || 'Failed to send OTP');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Verify password reset OTP
  verifyPasswordResetOTP: async (email: string, token: string) => {
    try {
      const { data, error } = await SupabaseAuth.verifyPasswordResetOTP(email, token);
      
      if (error) {
        throw error;
      }

      if (data.session) {
        // Store temporary session for password update
        localStorage.setItem('temp_reset_token', data.session.access_token);
        return {
          success: true,
          message: 'OTP verified successfully',
        };
      }

      return {
        success: false,
        message: 'Verification failed',
      };
    } catch (error: any) {
      message.error(error.message || 'OTP verification failed');
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Update password after OTP verification
  updatePassword: async (newPassword: string) => {
    try {
      const { data, error } = await SupabaseAuth.updatePassword(newPassword);
      
      if (error) {
        throw error;
      }

      // Clear temporary token
      localStorage.removeItem('temp_reset_token');
      
      message.success('Mật khẩu đã được cập nhật thành công');
      return {
        success: true,
        message: 'Password updated successfully',
      };
    } catch (error: any) {
      message.error(error.message || 'Failed to update password');
      return {
        success: false,
        message: error.message,
      };
    }
  },
};

export default authService;
